import { useCallback, useEffect, useState } from 'react';
import type { SavedHighlight } from 'modules/standardizedExam/components/runner/PassageHighlighter';
import { loadHighlights, saveHighlights, type HighlightsByPassage } from 'modules/standardizedExam/components/runner/highlightStore';

/** A regular test has no sections; all of its passages share this one. */
const TEST_SECTION = 'test';

interface State {
  attemptId: string | null;
  byPassage: HighlightsByPassage;
}

/**
 * Reading highlights for a regular test attempt, keyed by
 * `${questionId}:${pageIndex}`. Kept in this browser (same store as the SAT and
 * IELTS runners) so a reload or a jump to another question keeps them.
 */
export function usePassageHighlights(attemptId: string | null) {
  const [state, setState] = useState<State>(() => ({
    attemptId,
    byPassage: attemptId ? loadHighlights(attemptId, TEST_SECTION) : {},
  }));

  // The attempt id arrives after the attempt starts: load its highlights then.
  // Done during render so the save below never sees the previous attempt's list.
  let current = state;
  if (state.attemptId !== attemptId) {
    current = { attemptId, byPassage: attemptId ? loadHighlights(attemptId, TEST_SECTION) : {} };
    setState(current);
  }

  useEffect(() => {
    if (state.attemptId) saveHighlights(state.attemptId, TEST_SECTION, state.byPassage);
  }, [state]);

  const get = useCallback((key: string): SavedHighlight[] => current.byPassage[key] ?? [], [current.byPassage]);

  const update = useCallback(
    (key: string, change: (prev: SavedHighlight[]) => SavedHighlight[]) =>
      setState((prev) => ({ ...prev, byPassage: { ...prev.byPassage, [key]: change(prev.byPassage[key] ?? []) } })),
    [],
  );

  return { get, update };
}
